import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
} from '@angular/core';
import { AbstractControl, TouchedChangeEvent } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { filter, merge, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-form-error',
  templateUrl: './form-error.component.html',
  styleUrls: ['./form-error.component.scss'],
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormErrorComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly control = input<AbstractControl | null>();

  private readonly destroy$ = new Subject<void>();

  protected readonly valueMinLength = computed<{ value: number }>(() => ({
    value: this.control()?.getError('minlength')?.requiredLength,
  }));

  protected readonly valueMaxLength = computed<{ value: number }>(() => ({
    value: this.control()?.getError('maxlength')?.requiredLength,
  }));

  protected readonly valueMin = computed<{ value: number }>(() => ({
    value: this.control()?.getError('min')?.min,
  }));

  protected readonly valueMax = computed<{ value: number }>(() => ({
    value: this.control()?.getError('max')?.max,
  }));

  constructor() {
    effect(() => {
      const currentControl = this.control();
      this.destroy$.next();
      if (!currentControl) return;

      merge(currentControl.valueChanges, currentControl.statusChanges)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.cdr.markForCheck());

      currentControl.events
        .pipe(
          takeUntil(this.destroy$),
          filter((event) => event instanceof TouchedChangeEvent),
        )
        .subscribe(() => this.cdr.markForCheck());
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
